import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Image,
  Video,
  FileText,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Inbox,
  Plus,
  Lightbulb,
  Sparkles,
  Bug,
  Eye,
  ArrowRightCircle,
  XCircle,
  Archive,
  Camera,
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { WORK_ITEM_STATUSES } from '@yoda/shared';

interface Project {
  id: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  coverImageUrl: string | null;
  organizationName: string;
  status: string;
}

interface WorkItem {
  id: string;
  title: string;
  type: string;
  status: string;
  fileUrl: string | null;
  thumbnailUrl: string | null;
  commentCount: number;
}

interface RequestItem {
  id: string;
  title: string;
  type: 'idea' | 'feature' | 'bug';
  status: string;
  createdAt: string;
  commentCount: number;
}

const requestTypeConfig = {
  idea: { icon: Lightbulb, label: 'Idea', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  feature: { icon: Sparkles, label: 'Feature', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  bug: { icon: Bug, label: 'Bug', color: 'text-red-500', bg: 'bg-red-500/10' },
};

const requestStatusConfig: Record<string, { icon: typeof Inbox; label: string; color: string; bg: string }> = {
  new: { icon: Inbox, label: 'New', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  under_review: { icon: Eye, label: 'Under Review', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  plan_proposed: { icon: FileText, label: 'Plan Proposed', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  approved: { icon: CheckCircle2, label: 'Approved', color: 'text-green-500', bg: 'bg-green-500/10' },
  converted: { icon: ArrowRightCircle, label: 'Converted', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  declined: { icon: XCircle, label: 'Declined', color: 'text-red-500', bg: 'bg-red-500/10' },
  archived: { icon: Archive, label: 'Archived', color: 'text-gray-500', bg: 'bg-gray-500/10' },
};

const statusIcons: Record<string, typeof Clock> = {
  draft: FileText,
  seeking_commentary: MessageSquare,
  approval_requested: Clock,
  changes_requested: AlertCircle,
  approved: CheckCircle2,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1 },
};

export function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState<Project | null>(null);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'requests' | 'work-items'>('requests');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const [projectRes, workItemsRes, requestsRes] = await Promise.all([
          api.get<Project>(`/projects/${id}`),
          api.get<WorkItem[]>(`/projects/${id}/work-items${statusFilter ? `?status=${statusFilter}` : ''}`),
          api.get<RequestItem[]>(`/requests?projectId=${id}`) as any,
        ]);
        setProject(projectRes.data);
        setWorkItems(Array.isArray(workItemsRes.data) ? workItemsRes.data : []);
        setRequests(requestsRes.data || []);
        setError(null);
      } catch (err) {
        setError('Failed to load project');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    if (id) fetchData();
  }, [id, statusFilter]);

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info' | 'accent'> = {
      draft: 'default',
      seeking_commentary: 'warning',
      approval_requested: 'accent',
      changes_requested: 'danger',
      approved: 'success',
    };
    return variants[status] || 'default';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Project not found'}</p>
        <Link to="/" className="text-accent hover:underline">
          Back to Projects
        </Link>
      </div>
    );
  }

  // Count items by status
  const statusCounts = workItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      {/* Cover Image */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative -mx-4 lg:-mx-6 -mt-8"
      >
        <div className="relative w-full h-48 bg-bg-tertiary overflow-hidden">
          {project.coverImageUrl ? (
            <img
              src={project.coverImageUrl}
              alt={project.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/20 to-accent-light/10">
              <Camera className="w-12 h-12 text-text-muted" />
            </div>
          )}
        </div>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex gap-4">
            {/* Project Icon */}
            <div className="relative w-16 h-16 rounded-xl bg-bg-tertiary overflow-hidden flex-shrink-0 border-2 border-border">
              {project.iconUrl ? (
                <img
                  src={project.iconUrl}
                  alt={project.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/20 to-accent-light/10">
                  <span className="text-2xl font-bold text-accent">{project.name.charAt(0)}</span>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">{project.name}</h1>
                <Badge variant={project.status === 'active' ? 'success' : 'default'}>
                  {project.status}
                </Badge>
              </div>
              <p className="text-text-secondary mt-1">{project.organizationName}</p>
              {project.description && (
                <p className="text-text-muted mt-2">{project.description}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Link to={`/requests/new?projectId=${id}`}>
              <Button>
                <Plus className="w-4 h-4" />
                New Request
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="border-b border-border"
      >
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'requests'
                ? 'text-accent'
                : 'text-text-secondary hover:text-text'
            }`}
          >
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Requests
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-bg-tertiary">
                {requests.length}
              </span>
            </div>
            {activeTab === 'requests' && (
              <motion.div
                layoutId="activeProjectTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent"
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab('work-items')}
            className={`pb-3 px-1 font-medium transition-colors relative ${
              activeTab === 'work-items'
                ? 'text-accent'
                : 'text-text-secondary hover:text-text'
            }`}
          >
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Work Items
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-bg-tertiary">
                {workItems.length}
              </span>
            </div>
            {activeTab === 'work-items' && (
              <motion.div
                layoutId="activeProjectTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent"
              />
            )}
          </button>
        </div>
      </motion.div>

      {/* Status filters for Work Items */}
      {activeTab === 'work-items' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap gap-2"
        >
          <Button
            variant={statusFilter === null ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter(null)}
          >
            All ({workItems.length})
          </Button>
          {Object.entries(WORK_ITEM_STATUSES).map(([key, status]) => {
            const count = statusCounts[key] || 0;
            if (count === 0) return null;
            return (
              <Button
                key={key}
                variant={statusFilter === key ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setStatusFilter(key)}
              >
                <span
                  className="w-2 h-2 rounded-full mr-1.5"
                  style={{ backgroundColor: status.color }}
                />
                {status.label} ({count})
              </Button>
            );
          })}
        </motion.div>
      )}

      {/* Requests List */}
      {activeTab === 'requests' && (
        <>
          {requests.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Inbox className="w-12 h-12 mx-auto text-text-muted mb-4" />
              <h3 className="text-lg font-semibold mb-2">No requests yet</h3>
              <p className="text-text-secondary mb-4">
                Submit your first request for this project
              </p>
              <Link to={`/requests/new?projectId=${id}`}>
                <Button>
                  <Plus className="w-4 h-4" />
                  New Request
                </Button>
              </Link>
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-2"
            >
              {requests.map((request) => {
                const typeConfig = requestTypeConfig[request.type];
                const statusConf = requestStatusConfig[request.status];
                const TypeIcon = typeConfig?.icon || Lightbulb;
                const StatusIcon = statusConf?.icon || Inbox;

                return (
                  <motion.div key={request.id} variants={itemVariants}>
                    <Link to={`/requests/${request.id}`}>
                      <motion.div
                        whileHover={{ x: 4 }}
                        className="p-4 bg-bg-secondary rounded-xl border border-border hover:border-accent/30 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${typeConfig?.bg || 'bg-gray-500/10'}`}>
                            <TypeIcon className={`w-5 h-5 ${typeConfig?.color || 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">{request.title}</h3>
                            <p className="text-sm text-text-secondary">
                              {formatDate(request.createdAt)}
                            </p>
                          </div>
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConf?.bg || 'bg-gray-500/10'} ${statusConf?.color || 'text-gray-500'}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConf?.label || request.status}
                          </div>
                          {request.commentCount > 0 && (
                            <span className="flex items-center gap-1 text-xs text-text-muted">
                              <MessageSquare className="w-3 h-3" />
                              {request.commentCount}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </>
      )}

      {/* Work Items Grid */}
      {activeTab === 'work-items' && (
        <>
          {workItems.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Image className="w-12 h-12 mx-auto text-text-muted mb-4" />
              <h3 className="text-lg font-semibold mb-2">No work items yet</h3>
              <p className="text-text-secondary">
                Work items will appear here once OML Design uploads them.
              </p>
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            >
              {workItems.map((item) => {
                const StatusIcon = statusIcons[item.status] || FileText;
                const statusConfig = WORK_ITEM_STATUSES[item.status as keyof typeof WORK_ITEM_STATUSES];

                return (
                  <motion.div key={item.id} variants={itemVariants}>
                    <Link to={`/work-items/${item.id}`}>
                      <motion.div
                        whileHover={{ y: -4 }}
                        className="cursor-pointer"
                      >
                        <Card className="overflow-hidden group" glow>
                          {/* Thumbnail */}
                          <div className="relative aspect-video bg-bg-tertiary overflow-hidden">
                            {item.thumbnailUrl ? (
                              <motion.img
                                src={item.thumbnailUrl}
                                alt={item.title}
                                className="w-full h-full object-cover"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.3 }}
                              />
                            ) : item.type === 'image' && item.fileUrl ? (
                              <motion.img
                                src={item.fileUrl}
                                alt={item.title}
                                className="w-full h-full object-cover"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.3 }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {item.type === 'video' ? (
                                  <Video className="w-12 h-12 text-text-muted" />
                                ) : (
                                  <FileText className="w-12 h-12 text-text-muted" />
                                )}
                              </div>
                            )}

                            {/* Type indicator */}
                            <div className="absolute top-2 left-2">
                              <div className="p-1.5 rounded-lg bg-black/50 backdrop-blur-sm">
                                {item.type === 'image' && <Image className="w-4 h-4 text-white" />}
                                {item.type === 'video' && <Video className="w-4 h-4 text-white" />}
                                {item.type === 'document' && <FileText className="w-4 h-4 text-white" />}
                              </div>
                            </div>

                            {/* Actions overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>

                          {/* Content */}
                          <CardContent className="p-3">
                            <h3 className="font-medium truncate group-hover:text-accent transition-colors">
                              {item.title}
                            </h3>

                            <div className="flex items-center justify-between mt-2">
                              <Badge variant={getStatusBadgeVariant(item.status)} size="sm">
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig?.label || item.status}
                              </Badge>

                              {item.commentCount > 0 && (
                                <span className="flex items-center gap-1 text-xs text-text-muted">
                                  <MessageSquare className="w-3 h-3" />
                                  {item.commentCount}
                                </span>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
